import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Layout from '../components/Layout';
import { 
  Users, 
  CreditCard, 
  AlertTriangle, 
  TrendingUp,
  Calendar,
  ArrowUpRight,
  ArrowRight,
  Clock
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } }
};

const StatCard = ({ title, value, icon: Icon, trend, color, index }) => {
  const colorStyles = {
    indigo: { bg: 'from-indigo-500/12 to-indigo-600/5', icon: 'bg-indigo-500/15 text-indigo-400', glow: 'rgba(99, 102, 241, 0.1)' },
    emerald: { bg: 'from-emerald-500/12 to-emerald-600/5', icon: 'bg-emerald-500/15 text-emerald-400', glow: 'rgba(16, 185, 129, 0.1)' },
    amber: { bg: 'from-amber-500/12 to-amber-600/5', icon: 'bg-amber-500/15 text-amber-400', glow: 'rgba(245, 158, 11, 0.1)' },
  };
  
  const style = colorStyles[color] || colorStyles.indigo;

  return (
    <motion.div
      variants={item}
      className={`nova-card p-6 bg-gradient-to-br ${style.bg}`}
      style={{ '--glow-color': style.glow }}
    >
      <div className="flex items-start justify-between mb-5">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${style.icon}`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <div className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400">
            <ArrowUpRight className="w-3.5 h-3.5" />
            <span>{trend}</span>
          </div>
        )}
      </div>
      <p className="nova-stat-value">{value}</p>
      <p className="nova-stat-label mt-1">{title}</p>
    </motion.div>
  );
};

const AlertItem = ({ alert, index }) => {
  const isWarning = alert.type === 'warning';
  const bgClass = isWarning ? 'bg-amber-500/8 hover:bg-amber-500/12' : 'bg-red-500/8 hover:bg-red-500/12';
  const borderClass = isWarning ? 'border-amber-500/20' : 'border-red-500/20';
  const dotClass = isWarning ? 'nova-status-dot-warning' : 'nova-status-dot-danger';
  const textClass = isWarning ? 'text-amber-400' : 'text-red-400';

  return (
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.08, duration: 0.4 }}
    >
      <Link 
        to={`/clients/${alert.client_id}`}
        className={`flex items-center gap-4 p-4 rounded-xl ${bgClass} border ${borderClass} transition-all group`}
      >
        <div className={`nova-status-dot ${dotClass}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">{alert.client_name}</p>
          <p className={`text-xs ${textClass}`}>{alert.message}</p>
        </div>
        <ArrowRight className="w-4 h-4 text-[#48484d] group-hover:text-white group-hover:translate-x-1 transition-all" />
      </Link>
    </motion.div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [activity, setActivity] = useState({ recent_clients: [], recent_appointments: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [statsRes, alertsRes, revenueRes, activityRes] = await Promise.all([
        axios.get(`${API}/api/dashboard/stats`, { headers }),
        axios.get(`${API}/api/dashboard/alerts`, { headers }),
        axios.get(`${API}/api/dashboard/revenue-chart`, { headers }),
        axios.get(`${API}/api/dashboard/recent-activity`, { headers }),
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data);
      setRevenueData(revenueRes.data);
      setActivity(activityRes.data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(value);
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-8">
          <div className="h-10 w-48 nova-skeleton" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-36 nova-skeleton rounded-xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div 
        data-testid="dashboard" 
        className="space-y-10"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.div variants={item} className="flex items-end justify-between">
          <div>
            <h1 className="text-headline text-white">Dashboard</h1>
            <p className="text-caption mt-1">Resumen general de tu negocio</p>
          </div>
          <div className="hidden md:flex items-center gap-2 px-4 py-2 rounded-xl bg-[#161618] border border-white/[0.06]">
            <Clock className="w-4 h-4 text-[#6e6e73]" />
            <span className="text-sm text-[#a1a1a6]">
              {new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div 
          variants={container}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          <StatCard 
            title="Total Clientes" 
            value={stats?.total_clients || 0} 
            icon={Users} 
            color="indigo"
            index={0}
          />
          <StatCard 
            title="Membresías Activas" 
            value={stats?.active_memberships || 0} 
            icon={CreditCard} 
            color="emerald"
            index={1}
          />
          <StatCard 
            title="Por Vencer" 
            value={stats?.expiring_soon || 0} 
            icon={AlertTriangle} 
            color="amber"
            index={2}
          />
          <StatCard 
            title="Ingresos del Mes" 
            value={formatCurrency(stats?.monthly_revenue || 0)} 
            icon={TrendingUp} 
            trend="+12%"
            color="emerald"
            index={3}
          />
        </motion.div>

        {/* Charts and Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Chart */}
          <motion.div
            variants={item}
            className="lg:col-span-2 nova-card p-6"
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-title text-white">Ingresos Mensuales</h3>
                <p className="text-caption mt-0.5">Últimos 6 meses</p>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-medium">
                <TrendingUp className="w-3.5 h-3.5" />
                Tendencia positiva
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="100%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="month" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#6e6e73', fontSize: 12, fontWeight: 500 }}
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#6e6e73', fontSize: 12 }}
                    tickFormatter={(value) => `$${value/1000}k`}
                    dx={-10}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1c1c1f', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      color: '#ececef',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                      padding: '12px 16px'
                    }}
                    formatter={(value) => [formatCurrency(value), 'Ingresos']}
                    labelStyle={{ color: '#6e6e73', marginBottom: '4px' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#6366f1" 
                    strokeWidth={2.5}
                    fillOpacity={1} 
                    fill="url(#chartGradient)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Alerts */}
          <motion.div variants={item} className="nova-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-title text-white">Alertas</h3>
                <p className="text-caption mt-0.5">{alerts.length} pendientes</p>
              </div>
              {alerts.length > 0 && (
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                </span>
              )}
            </div>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {alerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-4">
                    <CreditCard className="w-7 h-7 text-emerald-400" />
                  </div>
                  <p className="text-sm font-medium text-white">Sin alertas</p>
                  <p className="text-xs text-[#6e6e73] mt-1">Todo en orden</p>
                </div>
              ) : (
                alerts.map((alert, i) => (
                  <AlertItem key={i} alert={alert} index={i} />
                ))
              )}
            </div>
          </motion.div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Today's Appointments */}
          <motion.div variants={item} className="nova-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-title text-white">Citas de Hoy</h3>
                <p className="text-caption mt-0.5">{stats?.todays_appointments || 0} programadas</p>
              </div>
              <Link to="/agenda" className="text-sm font-medium text-indigo-400 hover:text-indigo-300 flex items-center gap-1 transition-colors">
                Ver agenda <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-3">
              {activity.recent_appointments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[#1c1c1f] flex items-center justify-center mb-4">
                    <Calendar className="w-7 h-7 text-[#48484d]" />
                  </div>
                  <p className="text-sm text-[#6e6e73]">No hay citas programadas</p>
                </div>
              ) : (
                activity.recent_appointments.slice(0, 4).map((apt, i) => (
                  <motion.div 
                    key={i} 
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.08 }}
                    className="flex items-center gap-4 p-4 rounded-xl bg-[#111113] border border-white/[0.04] hover:border-white/[0.08] transition-all"
                  >
                    <div className="w-11 h-11 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{apt.date}</p>
                      <p className="text-xs text-[#6e6e73]">{apt.time}</p>
                    </div>
                    <span className="nova-badge nova-badge-neutral">
                      {apt.status === 'scheduled' ? 'Programada' : apt.status}
                    </span>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>

          {/* Recent Clients */}
          <motion.div variants={item} className="nova-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-title text-white">Clientes Recientes</h3>
                <p className="text-caption mt-0.5">Últimos registros</p>
              </div>
              <Link to="/clients" className="text-sm font-medium text-indigo-400 hover:text-indigo-300 flex items-center gap-1 transition-colors">
                Ver todos <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-3">
              {activity.recent_clients.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[#1c1c1f] flex items-center justify-center mb-4">
                    <Users className="w-7 h-7 text-[#48484d]" />
                  </div>
                  <p className="text-sm text-[#6e6e73]">No hay clientes recientes</p>
                </div>
              ) : (
                activity.recent_clients.map((client, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.08 }}
                  >
                    <Link 
                      to={`/clients/${client.id}`}
                      className="flex items-center gap-4 p-4 rounded-xl bg-[#111113] border border-white/[0.04] hover:border-indigo-500/30 transition-all group"
                    >
                      <div className="nova-avatar nova-avatar-md">
                        {client.name?.charAt(0)?.toUpperCase() || 'C'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate group-hover:text-indigo-300 transition-colors">{client.name}</p>
                        <p className="text-xs text-[#6e6e73]">
                          {new Date(client.created_at).toLocaleDateString('es-MX')}
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#48484d] group-hover:text-indigo-400 group-hover:translate-x-1 transition-all" />
                    </Link>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </Layout>
  );
}
